import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { showInZone, useZone } from '~/team_tools/components/timezone'
import { usePayoutWalletBalance } from '../api'
import { formatWalletAddress } from '../utils'
import { currencies } from './SelectCurrency'

/** Whether this wallet can pay everything still waiting.
 *
 *  On 2026-09-28 it held 1,111 USDC against 28 pending requests worth $2,141.69
 *  and nothing on the page said so, so a reviewer approving down the list would
 *  only have found out when a payout failed. */
const QueueCoverage = ({ balance, owed, requests }) => {
    if (owed == null || !requests) return null
    const short = Number(owed) - Number(balance ?? 0)
    return (
        <small
            className={short > 0 ? 'text-red-400 font-bold' : 'text-white/40'}
            title='Requested and approved withdrawals still waiting to be paid from this wallet, plus payouts that stopped with an error (they go back to the queue to be paid again)'
        >
            {short > 0
                ? `Queue ${Number(owed).toFixed(2)} (${requests}), short by ${short.toFixed(2)}`
                : `Queue ${Number(owed).toFixed(2)} (${requests}), covered`}
        </small>
    )
}

export const PayoutWalletBalance = ({ currency }) => {
    const usdc = currencies.find((c) => c.name === currency)
    const { data, isLoading, isSuccess, isError } = usePayoutWalletBalance({ currency: usdc.name })
    const [zone] = useZone()
    // Two places, like every other money figure on the page: the raw chain
    // value printed up to nine decimals (PAY-01). The reading's age is on the
    // card itself, in the Team Tools zone, because a cached balance that
    // looks live is how a reviewer approves into an empty wallet.
    const amount = data?.amount != null ? Number(data.amount).toFixed(2) : undefined
    const asOf = data?.updated_at ? showInZone(data.updated_at, zone) : null
    return (
        <div className='flex flex-col gap-1 items-center justify-center text-xs'>
            <div
                className='flex flex-row items-center gap-1 bg-white/10 rounded p-2 uppercase'
                title={asOf ? `Read from the chain ${asOf} (${zone})` : undefined}
            >
                <>
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : amount}
                    {isError && '...'}
                    <img src={usdc.img} alt={usdc.name} className='w-4 h-4' />
                </>
            </div>
            {isSuccess ? (
                <small title={data?.pubkey} className='text-white/30'>
                    {formatWalletAddress(data?.pubkey)}
                </small>
            ) : (
                <div>&nbsp;</div>
            )}
            {asOf && <small className='text-white/30'>as of {asOf}</small>}
            <QueueCoverage balance={data?.amount} owed={data?.owed} requests={data?.owed_requests} />
        </div>
    )
}
